import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Moon, ArrowLeft } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * Renders a soft "listening only" screen when the user has quiet_on_anniversary=true
 * and today is within ±1 day of a marked loss date. Otherwise renders children.
 */
export default function QuietModeGuard({ children }) {
  const { user } = useAuth();
  const [state, setState] = useState({ loaded: false, quiet: false, hit: null });

  useEffect(() => {
    if (!user?.quiet_on_anniversary) { setState({ loaded: true, quiet: false, hit: null }); return; }
    (async () => {
      try {
        const { data } = await api.get("/loss-dates");
        const hit = (data.anniversaries || []).find(a => Math.abs(a.days_until) <= 1);
        setState({ loaded: true, quiet: !!hit, hit });
      } catch { setState({ loaded: true, quiet: false, hit: null }); }
    })();
  }, [user?.quiet_on_anniversary]);

  if (!state.loaded) return null;
  if (!state.quiet) return children;

  const label = state.hit?.label ? `, ${state.hit.label}` : "";
  return (
    <div className="max-w-3xl mx-auto py-16 text-center" data-testid="quiet-screen">
      <div className="w-16 h-16 mx-auto rounded-full bg-sage-50 border border-sage-100 grid place-items-center">
        <Moon size={28} weight="duotone" className="text-sage-700" />
      </div>
      <div className="text-xs tracking-[0.25em] uppercase text-sage-700 mt-6">Quiet mode</div>
      <h1 className="font-display text-4xl sm:text-5xl leading-tight mt-3">
        Today is tender{label}.
      </h1>
      <p className="mt-5 text-ink/70 max-w-lg mx-auto leading-relaxed">
        You asked us to keep things quiet around your anniversary, so matches and messages
        are hidden today. Everything's still here when you're ready. No rush.
      </p>
      <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
        <Link to="/app" data-testid="quiet-timeline"
              className="inline-flex items-center gap-2 bg-ink text-cream px-5 py-2.5 rounded-full text-sm hover:bg-coral transition-colors">
          <ArrowLeft size={14} /> Back to timeline
        </Link>
        <Link to="/app/navigator" data-testid="quiet-navigator"
              className="inline-flex items-center gap-2 border border-border px-5 py-2.5 rounded-full text-sm hover:border-ink transition-colors">
          Talk to the Navigator
        </Link>
      </div>
      <p className="mt-8 text-xs text-muted-foreground">
        You can turn Quiet Mode off any time from the Pregnancy Loss hub.
      </p>
    </div>
  );
}
